import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { Award, Download, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import LinkedInShare from "@/components/LinkedInShare";
import { track } from "@/lib/analytics";

export default function Certificados() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/alumno/certificates").then((r) => setItems(r.data));
  }, []);

  const copyLink = async (folio) => {
    const url = `${window.location.origin}/verificar/${folio}`;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url); ok = true;
      }
    } catch { ok = false; }
    if (!ok) {
      const ta = document.createElement("textarea"); ta.value = url;
      ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta);
      ta.select(); ok = document.execCommand("copy"); document.body.removeChild(ta);
    }
    if (ok) { setCopied(folio); toast.success("Link copiado"); setTimeout(() => setCopied(null), 1400); }
    else toast.error("No se pudo copiar");
  };

  const downloadPdf = async (folio) => {
    try {
      const r = await api.get(`/alumno/certificates/${folio}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `certificado-${folio}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      track("certificate_downloaded", { folio });
    } catch { toast.error("No se pudo descargar el PDF"); }
  };

  return (
    <AlumnoLayout user={user}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Certificados</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Tus certificados</h1>
        <p className="mt-2 text-gray-400">Descarga tus certificados PDF y comparte el link público de verificación.</p>

        <div className="mt-8">
          {items.length === 0 ? (
            <div className="p-12 border-2 border-dashed border-gray-800 rounded-2xl text-center animate-fade-in-up" data-testid="certs-empty">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#93C5FD]/10 border border-[#93C5FD]/30 flex items-center justify-center">
                <Award className="w-8 h-8 text-[#93C5FD]" />
              </div>
              <p className="mt-4 text-lg font-semibold text-white">Aún no tienes certificados</p>
              <p className="mt-1 text-sm text-gray-400 max-w-sm mx-auto">
                Cuando completes un programa, tu certificado aparecerá aquí con folio de verificación pública.
              </p>
              <Link to="/app/alumno/programas" data-testid="certs-empty-cta"
                className="mt-5 inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold transition-all hover:scale-[1.02]">
                Ver mis programas
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="certificates-list">
              {items.map((c) => (
                <div key={c.id} className="p-5 bg-[#111827] border border-gray-800 rounded-xl">
                  <div className="flex items-start justify-between">
                    <Award className="w-8 h-8 text-[#93C5FD]" />
                    <span className="text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5">Válido</span>
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-white">{c.program_title}</h3>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">{c.program_format}</p>
                  <p className="mt-3 font-mono text-sm text-white" data-testid={`cert-folio-${c.folio}`}>{c.folio}</p>
                  <p className="text-xs text-gray-500 mt-1">Emitido: {new Date(c.issued_at).toLocaleDateString("es-MX")}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => downloadPdf(c.folio)} data-testid={`cert-download-${c.folio}`}
                      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white">
                      <Download className="w-3 h-3" /> Descargar PDF
                    </button>
                    <button onClick={() => copyLink(c.folio)} data-testid={`cert-copy-${c.folio}`}
                      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5">
                      {copied === c.folio ? <><Check className="w-3 h-3 text-emerald-400" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar link</>}
                    </button>
                    <a href={`/verificar/${c.folio}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5">
                      <ExternalLink className="w-3 h-3" /> Ver verificación
                    </a>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-800">
                    <LinkedInShare folio={c.folio} programTitle={c.program_title} issuedAt={c.issued_at} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AlumnoLayout>
  );
}
